import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// ** MUI Imports
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";

import Typography from "@mui/material/Typography";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Lessons from "./Lessons";
import { styled } from "@mui/material/styles";
import MuiAccordion from "@mui/material/Accordion";
import moment from "moment/moment";
import { locale } from "src/lang/locale/moment_pt_PT";
import ChevronDown from "mdi-material-ui/ChevronDown";
import ArrowCollapseRight from "mdi-material-ui/ArrowCollapseRight";
import Tooltip from "@mui/material/Tooltip";
import ChapterProgress from "./ChapterProgress";
import { isChapterAvailable, ChapterAvaliableIn } from "./helper";

moment.locale("pt", locale);

// Styled component for Accordion component.
// Defined at module scope (not inside the component) so its identity is stable:
// otherwise a new component type is created on every render, remounting the whole
// accordion subtree on each lesson click and replaying the module transition.
const Accordion = styled(MuiAccordion)(({ theme }) => ({
  boxShadow: "none !important",
  // border: `1px solid ${theme.palette.divider}`,
  borderRadius: "0px",
  "&:not(:last-of-type)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "2px",
  },
  "&:first-of-type": {
    "& .MuiButtonBase-root": {
      borderTopLeftRadius: "0px",
      borderTopRightRadius: "0px",
    },
  },
  "&:last-of-type": {
    "& .MuiAccordionSummary-root:not(.Mui-expanded)": {
      borderBottomLeftRadius: "0px",
      borderBottomRightRadius: "0px",
    },
  },
}));

function DesktopPlayList({
  videos_info,
  details,
  actualChapter,
  actualLesson,
  lessonStatusHandler,
  activeLessonHandler,
  onShowAttachmentsClick,
  lessonsStatus,
  courseAddedAt,
  courseEndDate,
  showLessonNumbers,
  showChapterNumbers,
  onCollapseListClick,
  hasTeaserAccess = false,
}) {
  const [searchParams] = useSearchParams();
  const isIframeMode = searchParams.get("mode") === "iframe";

  const marginOffset = isIframeMode ? 0 : 76;
  const marginTopOffset = isIframeMode ? 0 : 76;
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState({}); //`panel${actualChapter}`

  // Space taken by the playlist header ("Conteúdo do curso" + collapse button).
  // Without subtracting this, the scroll area height can exceed the fixed card height and cut the last item.
  const headerOffsetPx = isIframeMode ? 56 : 0;

  useEffect(() => {
    const onScroll = () => setOffset(window.pageYOffset);

    // clean up code
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    setExpanded((prev) => ({ ...prev, [`panel${actualChapter}`]: true }));

    return () => window.removeEventListener("scroll", onScroll);
  }, [actualChapter]);

  useEffect(() => {
    setExpanded((prev) => ({ ...prev, [`panel${actualChapter}`]: true }));
  }, [actualChapter]);

  const offsetY = Math.abs(marginOffset - offset) < 0 ? 0 : Math.abs(marginOffset - offset);

  const handleChange = (panel) => (event, isExpanded) => {
    const _panel = isExpanded ? true : false;

    const r = { ...expanded };
    //Set All Panels to false except the one that was clicked
    Object.keys(r).forEach((key) => {
      if (key !== panel) {
        r[key] = false;
      }
    });
    //Set the clicked panel to true
    r[panel] = _panel;
    //Set the expanded state
    setExpanded(r);
  };

  return (
    <Card
      className="playlist-container"
      style={{
        top: offset < marginOffset ? offsetY : 0,
        height: "100%",
      }}
    >
      {/* pl matches the module card's 14px left margin (.side-bar-accordion) so the
          hide button lines up with the cards; theme spacing is 0.25rem/factor, not 8px */}
      <Box sx={{ pl: "14px", pr: 2, pt: 2.5, pb: 1.5 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Esconder lista de conteúdos" placement="top">
            <button type="button" className="playlist-icon-btn" aria-label="Esconder lista de conteúdos" onClick={onCollapseListClick}>
              <ArrowCollapseRight />
            </button>
          </Tooltip>
          <h4 className="playlist-title" style={{ marginLeft: 12 }}>Conteúdo do curso</h4>
        </div>
      </Box>
      <div
        className="scrollbar"
        style={{
          height: `calc(100% - ${headerOffsetPx + (offset < marginOffset ? offsetY + marginTopOffset : marginTopOffset)}px)`,
          overflowY: "scroll",
          display: "flex",
          flexFlow: "column",
          paddingBottom: 8,
        }}
      >
        {details?.map((e, i) => {
          const lessonsCounter = e?.lessons?.length;

          const chapterLessonsStatus = lessonsStatus.filter((el) => e.lessons.find((lesson) => lesson.lesson_id === el.lesson_id));

          let completedLessons = parseInt(chapterLessonsStatus.filter((el) => el.completed === true)?.length || 0);
          let completedPercentage = parseInt((completedLessons / lessonsCounter) * 100);
          if (completedPercentage > 100) completedPercentage = 100;
          if (completedPercentage < 0) completedPercentage = 0;
          return (
            <Accordion
              className="side-bar-accordion"
              key={e.chapter_id}
              sx={{ p: 2 }}
              expanded={expanded[`panel${e.chapter_id}`] || false}
              onChange={handleChange(`panel${e.chapter_id}`)}
              // Consistent, slightly eased collapse so switching modules is not abrupt
              TransitionProps={{ timeout: 350 }}
            >
              <AccordionSummary
                // style={{
                //   backgroundColor: e.chapter_id === actualChapter ? hexToRGBA(selectedColor, 0.06) : "",
                // }}
                className={`side-bar-accordion-summary ${e.chapter_id === actualChapter ? "side-bar-accordion-active" : ""}`}
                id={`panel-header-${e.chapter_id}`}
                aria-controls={`panel-content-${e.chapter_id}`}
                expandIcon={<ChevronDown />}
              >
                <div className={`side-bar-accordion-summary-container ${e.chapter_id === actualChapter ? "active" : ""}`}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontSize: 16 }}>
                    <div className="chapter-container">
                      <div className="chapter-image-title">
                        {e.image !== null && e.image !== "" && e.image !== undefined && <img className="chapter-image" src={e.image} alt={e.title} />}
                        <div>
                          {showChapterNumbers && <h6>MÓDULO {i + 1}</h6>}
                          {e.title}
                          {isChapterAvailable(e, courseAddedAt, hasTeaserAccess) && <ChapterProgress completedPercentage={completedPercentage} />}
                          <ChapterAvaliableIn
                            chapter={e}
                            courseAddedAt={courseAddedAt}
                            courseEndDate={courseEndDate}
                            hasTeaserAccess={hasTeaserAccess}
                          />
                        </div>
                      </div>
                    </div>
                  </Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className="side-bar-accordion-details">
                <div className="side-bar-accordion-details-container">
                  <Lessons
                    isChapterAvailable={isChapterAvailable(e, courseAddedAt, hasTeaserAccess)}
                    lessonsStatus={lessonsStatus}
                    videos_info={videos_info}
                    chapter={e?.chapter_id}
                    lessons={e?.lessons}
                    actualChapter={actualChapter}
                    actualLesson={actualLesson}
                    lessonStatusHandler={lessonStatusHandler}
                    activeLessonHandler={activeLessonHandler}
                    showLessonNumbers={showLessonNumbers}
                    onShowAttachmentsClick={onShowAttachmentsClick}
                  />
                </div>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </div>
    </Card>
  );
}

export default DesktopPlayList;
